import React, { useEffect } from 'react';
import { FaTrash, FaPlus, FaEdit, FaGripVertical, FaCopy } from 'react-icons/fa';
import { Language_Text_Editor } from './language_text_editor.js';
import {
  DragDropContext,
  Droppable,
  Draggable
} from 'react-beautiful-dnd';
import { Colors, generateHierarchicalCodes } from './form_builder.js';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";
import EditorJSRenderer from './components/EditorJSRenderer.js';
import { formBuilderLabels } from '../../_label/form_builder/form_builder_labels.js';

/**
 * Questions_Builder component with drag & drop
 *
 * @param {Array} questions
 * @param {string} language
 * @param {function} onTextChange (index, updatedTextArray)
 * @param {function} onDelete (index)
 * @param {function} onAdd ()
 * @param {function} onReorder (reorderedQuestions)
 * @param {function} onEdit (index) - optional
 */
const Questions_Builder = ({ 
  questions, 
  language, 
  onTextChange, 
  onDelete, 
  onAdd, 
  onReorder, 
  onEdit,
  onClone,
  colors = Colors,
  isEditing = false,
  parentEditContext = null,
  parentSectionCode = null
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);
  }, []);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reordered = Array.from(questions);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Update question codes and answer codes after reordering
    if (parentSectionCode) {
      const updatedQuestions = reordered.map((question, index) => {
        const newQuestionCode = generateHierarchicalCodes.generateQuestionCode(parentSectionCode, index);
        
        // Also regenerate answer codes for this question
        const updatedAnswers = (question.answers || []).map((answer, aIndex) => ({
          ...answer,
          answerCode: generateHierarchicalCodes.generateAnswerCode(newQuestionCode, aIndex)
        }));
        
        return {
          ...question,
          questionCode: newQuestionCode,
          answers: updatedAnswers
        };
      });
      onReorder(updatedQuestions);
    } else {
      onReorder(reordered);
    }
  };

  // Select appropriate contextual colors
  let editContextColors = colors.editContextQuestion;
  if (parentEditContext === 'section') {
    editContextColors = colors.editContextSection;
  }

  // Determine visual style based on context
  const getBorderStyle = () => {
    if (!isEditing) return {};
    return { 
      borderColor: editContextColors.border, 
      borderWidth: parentEditContext ? '1px' : '2px' 
    };
  };

  const getBodyStyle = () => {
    if (!isEditing) return {};
    return { backgroundColor: editContextColors.body };
  };

  return (
    <div className="card shadow-sm w-100" style={getBorderStyle()}>
      <div className="card-header d-flex justify-content-between align-items-center"
        style={{ backgroundColor: colors.secondaryLight }}
      >
        <h6 className="mb-0">
          {isEditing && !parentEditContext && 
            <span className="badge me-2" style={{ backgroundColor: editContextColors.badge, color: colors.white }}>
              {t('editing', { ns: 'labels' })}
            </span>
          }
          {t('questions', { ns: 'labels' })}
        </h6>
        <button 
          className="btn btn-sm"
          style={{ 
            backgroundColor: colors.white, 
            color: colors.dark 
          }}
          onClick={() => {
            onAdd();
            // Question code will be assigned after the new question is added
            // This is handled in section_editor.js
          }}
          title={t('addQuestion', { ns: 'labels' })}
        >
          <FaPlus className="me-1" />
          {t('addQuestion', { ns: 'labels' })}
        </button>
      </div>

      <div className="card-body p-3" style={getBodyStyle()}>
        {questions.length === 0 ? (
          <div className="text-center py-4 text-muted w-100">
            <p>{t('noQuestions', { ns: 'labels' })}</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="questions">
              {(provided, snapshot) => (
                <div 
                  ref={provided.innerRef} 
                  {...provided.droppableProps}
                  className="questions-list w-100"
                  style={{
                    transition: 'background-color 0.2s ease',
                    backgroundColor: snapshot.isDraggingOver ? colors.primaryLight : 'transparent'
                  }}
                >
                  {questions.map((question, index) => (
                    <Draggable 
                      key={`question-${question.id || index}`} 
                      draggableId={`question-${question.id || index}`} 
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          className={`card mb-3 w-100 ${snapshot.isDragging ? 'border-primary' : ''}`}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            transition: snapshot.isDragging ? 'transform 0.2s ease, box-shadow 0.2s ease' : 'none',
                            ...(snapshot.isDragging ? { 
                              borderColor: colors.primary,
                              cursor: 'grabbing',
                              boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                              zIndex: 1000
                            } : {
                              transform: 'none',
                              transition: 'none'
                            }),
                            // Apply reduced opacity for invisible questions
                            opacity: question.invisible ? 0.6 : 1,
                          }}
                        >
                          <div className="card-body py-2 px-3">
                            <div className="row align-items-start w-100 g-0">
                              <div className="col-auto me-3 mt-1" {...provided.dragHandleProps}>
                                <FaGripVertical className="text-muted" style={{ cursor: 'grab' }} />
                              </div>
                              <div className="col">
                                <div className="mb-2">
                                  <EditorJSRenderer
                                    content={question.questionText?.find(t => t.language === language)?.text || ''}
                                    language={language}
                                    defaultText={`Question ${index + 1}`}
                                  />
                                </div>
                                <div className="text-muted small d-flex flex-wrap">
                                  {question.questionCode && (
                                    <span className="me-3">{t('code', { ns: 'labels' })}: <strong>{question.questionCode}</strong></span>
                                  )}
                                  {question.questionType && (
                                    <span className="me-3">
                                      {t('type', { ns: 'labels' })}: <strong>{
                                        question.questionType === 'single' ? t('singleChoice', { ns: 'labels' }) : 
                                        question.questionType === 'multiple' ? t('multipleChoice', { ns: 'labels' }) : 
                                        question.questionType === 'open' ? t('openAnswer', { ns: 'labels' }) : 
                                        question.questionType === 'openNumber' ? t('openNumberAnswer', { ns: 'labels' }) : 
                                        question.questionType === 'date' ? t('date', { ns: 'labels' }) :
                                        question.questionType === 'time' ? t('time', { ns: 'labels' }) :
                                        question.questionType === 'datetime' ? t('datetime', { ns: 'labels' }) :
                                        question.questionType === 'departmentFunctionalArea' ? t('departmentFunctionalArea', { ns: 'labels' }) :
                                        question.questionType
                                      }</strong>
                                    </span>
                                  )}
                                  <span className="me-3">
                                    {t('mandatory', { ns: 'labels' })}: <strong>{question.mandatory ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}</strong>
                                  </span>
                                  <span className="me-3">
                                    {t('invisible', { ns: 'labels' })}: <strong>{question.invisible ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}</strong>
                                  </span>
                                  <span className="me-3">
                                    {t('files', { ns: 'labels' })}: <strong>{question.files?.length || 0}</strong>
                                  </span>
                                  <span className="me-3">
                                    {t('answers', { ns: 'labels' })}: <strong>{question.answers?.length || 0}</strong>
                                  </span>
                                  {question.visibilityCondition && (
                                    <span>
                                      {t('condition', { ns: 'labels' })}: <strong>{t('yes', { ns: 'labels' })}</strong>
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-auto ms-3">
                                <div className="btn-group">
                                  <button
                                    className="btn btn-sm"
                                    style={{ 
                                      color: colors.info,
                                      borderColor: colors.info,
                                      backgroundColor: colors.white 
                                    }}
                                    title={t('editQuestion', { ns: 'labels' })}
                                    onClick={() => {
                                      // Save any pending local changes before entering editor
                                      // The onTextChange should have already been called by Language_Text_Editor
                                      onEdit?.(index);
                                    }}
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    style={{ 
                                      color: colors.success,
                                      borderColor: colors.success,
                                      backgroundColor: colors.white 
                                    }}
                                    title={t('cloneQuestion', { ns: 'labels' })}
                                    onClick={() => onClone?.(index)}
                                  >
                                    <FaCopy />
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    style={{ 
                                      color: colors.danger,
                                      borderColor: colors.danger,
                                      backgroundColor: colors.white 
                                    }}
                                    title={t('deleteQuestion', { ns: 'labels' })}
                                    onClick={() => onDelete(index)}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export { Questions_Builder };

